import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import * as _ from 'underscore';
import { Observable } from 'rxjs';
import { BasePaylineEditComponent } from '../base-payline-edit/base-payline-edit.component';
import { Tax } from '../../../../models/payroll';
import { CompanyTaxCodeConfig } from '../../../../models/tax-config';

@Component({
  selector: 'payline-tax-row',
  templateUrl: './payline-tax-row.component.html',
  styleUrls: ['./payline-tax-row.component.css'],
  standalone: false
})
export class PaylineTaxRowComponent extends BasePaylineEditComponent implements OnInit {
  @Input() tax: Tax;
  @Input() config: CompanyTaxCodeConfig;
  @Input() clearTaxesEvent: Observable<void>;
  @Input() rowIndex: number;
  @Input() lastRowIndex: number;
  @Output() onEdit: EventEmitter<Tax> = new EventEmitter<Tax>();

  // form group to manage our user inputs
  public taxForm = new UntypedFormGroup({
    fixedAdditional: new UntypedFormControl(''),
    rateAmount: new UntypedFormControl(''),
    total: new UntypedFormControl('')
  });

  get t() {
    return this.taxForm.controls;
  }

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.clearTaxesEvent.subscribe(() => this._onWithholdingTaxesCleared());

    this._setTaxValues(this.tax);
    this._configureControls();

    this._onRateAmountChange = _.debounce(this._onRateAmountChange, 500);
  }

  // NOTE: object reference is broken from the payroll data model.
  // when changes to the current amount are detected,
  // (from Preview Totals), update the total field
  ngDoCheck() {
    // NOTE: when blank, this.deductionForm.get('total') is an empty string
    let value = this.taxForm.get('total').value || null;
    // CurrentAmount can be null or 0
    let currentAmount = this.tax.CurrentAmount || null;
    if (currentAmount != value) {
      this.taxForm.get('total').setValue(this.tax.CurrentAmount);
    }
  }

  // stop arrow keys from changing the value of this drop down
  // this is going to confuse/upset at least one user,
  // but there's not other way to do keyboard navigation on this input
  public onFixedAdditionalChangeKeydown(evnt) {
    if (this.navigationKeys.includes(evnt.code)) {
      evnt.preventDefault();
      return false;
    }
    return null;
  }

  // handle keyboard navigation across payline detail editor
  public onFixedAdditionalChangeKeyup(evnt) {
    // attempt to let users to keyboard navigate in the detail editor
    if (this.navigationKeys.includes(evnt.code)) {
      this.onKeyboardNavigate(
        evnt.code,
        this.rowIndex,
        2, // fixed/additional is at focusable column index 2
        this.lastRowIndex
      );
    }
  }

  // handle just the value change, no keyboard navigation logic
  public onFixedAdditionalChange(evnt) {
    this.tax.TemporaryCalculationType = evnt?.target?.value;

    // if calculation type is cleared, clear the amount value
    if (!this.tax.TemporaryCalculationType || this.tax.TemporaryCalculationType == 'None') {
      this.tax.Amount = 0;
      this.tax.TemporaryAmount = null;
      this.taxForm.get('rateAmount').disable();
      this.taxForm.get('rateAmount').setValue('');
    } else {
      this.taxForm.get('rateAmount').enable();

      if (this.tax.TemporaryAmount == null) {
        this.tax.TemporaryAmount = 0;
        this.taxForm.get('rateAmount').setValue('0');
      }
    }

    this.onEdit.emit(this.tax);
  }

  public onRateAmountChange(evnt) {
    // attempt to let users to keyboard navigate in the detail editor
    if (this.navigationKeys.includes(evnt.code)) {
      this.onKeyboardNavigate(
        evnt.code,
        this.rowIndex,
        3, // rate/amount is at focusable column index 3
        this.lastRowIndex
      );
    }

    this._onRateAmountChange(evnt);
  }

  // #region Private Method

  private _configureControls() {
    const fixedCtrl = this.taxForm.get('fixedAdditional');
    const rateCtrl = this.taxForm.get('rateAmount');
    // task 8963 - if config tax type is "I" (income), tax can be edited
    // task 10088 - if the tax model specifies override, allow it
    if (this.config?.TaxType == 'I' || this.tax.AllowOverrideSecondPays) {
      fixedCtrl.enable();
      rateCtrl.enable();
    } else {
      fixedCtrl.disable();
      rateCtrl.disable();
    }

    // task 8963 - if a calculation type is not set, disable rate field
    if (!fixedCtrl.value || fixedCtrl.value == 'None') {
      rateCtrl.disable();
    }
  }

  private _onRateAmountChange(evnt) {
    // NOTE: if value is blank, pass 0 to the API
    // to ensure totals are returned properly
    this.tax.TemporaryAmount = evnt?.target?.value || 0;

    // Bug 9072: in some scenarios, the user might be able to override the amount before they've
    // actually changed the calculation type, because the regular calc type could already be set
    // to Fixed or similar.  in those cases, we need to set TemporaryCalcMethod to the
    // already-selected value  --nk 2022-01-27
    if (this.tax.TemporaryCalculationType === null)
      this.tax.TemporaryCalculationType = this.tax.CalculationType;

    this.onEdit.emit(this.tax);
  }

  private _onWithholdingTaxesCleared() {
    // only clear values if the tax we're editing is a withholding type
    if (this.config?.TaxType == 'I') {
      this.tax.TemporaryAmount = 0;
      this.tax.TemporaryCalculationType = 'Fixed';
      this.tax.CurrentAmount = 0; // so total field updates immediately

      // ensure the rate field is enabled for edit
      this.taxForm.get('rateAmount').enable();

      this._setTaxValues(this.tax);

      this.onEdit.emit(this.tax);
    }
  }

  private _setTaxValues(tax: Tax): void {
    let fixedAdditional = this.tax.TemporaryCalculationType ?? this.tax.CalculationType;
    if (fixedAdditional == null || fixedAdditional === '') {
      fixedAdditional = 'None';
    }

    this.taxForm.get('fixedAdditional').setValue(fixedAdditional);

    this.taxForm
      .get('rateAmount')
      // NOTE: use ?? rather than || to ensure TemporaryAmount's of 0 override permanent amounts
      .setValue(this.tax.TemporaryAmount ?? this.tax.Amount ?? '');

    this.taxForm.get('total').setValue(this.tax.CurrentAmount);
  }

  // #endregion
}
